import React, { useCallback, useEffect, useRef } from 'react';

/**
 * The §4.10 single drill destination — one body-agnostic stacked slide-over that
 * every drill on the page opens into, instead of a sixth bespoke slide-out. A
 * consolidation, not greenfield: the five existing slide-outs (SessionDetailPanel,
 * RecoveryPanel, PlaybookDetailSlideout, CompetitorDetailDrawer, RateDetailDrawer)
 * are neither deleted nor rewritten.
 *
 * Behaviour (all §4.10):
 *   - Right slide-over on tablet+, full-screen sheet below `md` (768px).
 *   - Scrim click and Escape both close everything (`onClose`).
 *   - Stacks: `stack=[{ kind, label, rows }]`, top frame = last. A back affordance
 *     appears once there is more than one frame and pops via `onBack`.
 *   - NEVER navigates away — it is a fixed overlay, no routing. An owner who set an
 *     as-of date keeps it.
 *   - Focus-trapped while open; returns focus to the trigger on close.
 *
 * Body:
 *   - By default renders the Search Console row shape `{ primary, secondary,
 *     metric, drill }` as a list; a row carrying a `drill` is a button that calls
 *     `onRowDrill(row)` (the parent runs `fn_drill` and pushes the next frame). A
 *     row without a `drill` is static — that is what ends the stack.
 *   - `renderBody(frame)` overrides the default for other domains (the Conversation
 *     build passes its own detail panes).
 *
 * @param {Array} props.stack frames, top is the last element
 * @param {Function} props.onBack pop one frame
 * @param {Function} props.onClose close all frames
 * @param {Function} [props.onRowDrill] called with a drillable row on click
 * @param {Function} [props.renderBody] custom body renderer `(frame) => node`
 * @param {boolean} [props.loading] the top frame's rows are still loading
 */
const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/** A value that would put something on screen: not null, not an empty or blank string. */
const hasText = (v) => v != null && (typeof v !== 'string' || v.trim() !== '');

/**
 * Whether a default-body row shows anything at all. A row whose `primary`, `secondary`
 * and `metric` are all empty renders as a bare divider — a line with no text — so it is
 * dropped, and a frame left with no such rows says there are no records instead of
 * drawing a column of empty lines.
 */
export const hasContent = (row) => Boolean(row) && (hasText(row.primary) || hasText(row.secondary) || hasText(row.metric));

const DrillOver = ({ stack, onBack, onClose, onRowDrill, renderBody, loading = false }) => {
  const panelRef = useRef(null);
  // Captured once when the overlay opens so focus can return to whatever opened it.
  const returnFocusRef = useRef(null);
  const frames = Array.isArray(stack) ? stack : [];
  const isOpen = frames.length > 0;
  const top = isOpen ? frames[frames.length - 1] : null;
  const canGoBack = frames.length > 1;

  useEffect(() => {
    if (!isOpen) return undefined;
    returnFocusRef.current = document.activeElement;
    // Move focus into the panel so the trap has somewhere to start and a keyboard
    // reader is not left behind the scrim.
    const panel = panelRef.current;
    const first = panel?.querySelector(FOCUSABLE);
    (first || panel)?.focus();
    return () => {
      // Return focus on close. `preventScroll` so the page does not jump back.
      const el = returnFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
    };
    // Re-run only on open/close, not on every stack push — a deeper frame should
    // not steal focus back to the top of the panel mid-read.
  }, [isOpen]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose?.();
      return;
    }
    if (e.key !== 'Tab') return;
    // Focus trap: wrap Tab / Shift+Tab within the panel.
    const panel = panelRef.current;
    if (!panel) return;
    const nodes = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
      (n) => n.offsetParent !== null || n === document.activeElement,
    );
    if (nodes.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const firstEl = nodes[0];
    const lastEl = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  }, [onClose]);

  if (!isOpen) return null;

  const rows = (Array.isArray(top?.rows) ? top.rows : []).filter(hasContent);

  return (
    <div className="fixed inset-0 z-50" role="presentation">
      {/* Scrim — click closes everything. */}
      <button
        type="button"
        aria-label="Close details"
        data-testid="drill-scrim"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px] cursor-default"
        tabIndex={-1}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={top?.label || top?.title || 'Details'}
        data-testid="drill-over"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="absolute bg-white dark:bg-gray-900 shadow-2xl flex flex-col outline-none
                   inset-x-0 bottom-0 top-0 md:inset-y-0 md:right-0 md:left-auto
                   w-full md:w-[920px] md:max-w-[calc(100vw-60px)]"
      >
        {/* Header — back (when stacked), label, close. */}
        <div className="flex items-center gap-3 px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          {canGoBack && (
            <button
              type="button"
              onClick={onBack}
              data-testid="drill-back"
              className="p-1.5 -ml-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
              aria-label="Back"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          )}
          {/* Superset header: Search Console frames carry `label`; the Conversation
              build's frames carry `title` (+ optional `subtitle`), or a ready
              `titleNode` (a rich header rendered in place of the plain title). */}
          <div className="min-w-0 flex-1">
            {top?.titleNode ? (
              top.titleNode
            ) : (
              <>
                <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {top?.label || top?.title || 'Details'}
                </h2>
                {top?.subtitle && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{top.subtitle}</p>
                )}
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="drill-close"
            aria-label="Close"
            className="p-1.5 -mr-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
          {/* Superset body: the Conversation build carries a ready `body` node in the
              frame (a detail pane, a guest profile, a set list); Search Console passes
              `rows` + `renderBody`. Prefer a carried body, else fall back to the
              rows/renderBody path. */}
          {top?.body != null ? (
            top.body
          ) : renderBody ? (
            renderBody(top)
          ) : loading ? (
            <div className="space-y-2" role="status" aria-label="Loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-12 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
              ))}
              <span className="sr-only">Loading…</span>
            </div>
          ) : rows.length === 0 ? (
            <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
              No records to show for this selection.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-800" data-testid="drill-rows">
              {rows.map((row, i) => {
                const drillable = Boolean(row?.drill) && typeof onRowDrill === 'function';
                const content = (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">{row.primary}</div>
                      {row.secondary && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 break-words">{row.secondary}</div>
                      )}
                    </div>
                    {row.metric != null && (
                      <div className="shrink-0 text-sm font-semibold tabular-nums text-[#32B1FF]">{row.metric}</div>
                    )}
                    {drillable && (
                      <svg className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    )}
                  </>
                );
                if (drillable) {
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => onRowDrill(row)}
                        className="w-full flex items-center gap-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset transition-colors"
                      >
                        {content}
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={i} className="flex items-center gap-3 py-3">
                    {content}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrillOver;
